import type { ExperimentChecker } from "./ExperimentChecker";
import { checkExperimentBase } from "./ExperimentChecker";
import {
  staticRibbonExperiments,
  isShyRibbonFeatureEnabled as isShyRibbonFeatureEnabledGeneric,
} from "./staticRibbonExperiments";
import { fluentExperiments } from "./fluentExperiments";

const react18Experiments = {
  react18RenderNotificationsFixEnabled:
    "Microsoft.Office.SharedOnline.React18RenderNotificationsFixEnabled",
  react18RenderIconFixEnabled:
    "SharedOnline.ChangeGate.React18RenderIconFixEnabledV2",
  react18RenderTooltipFixEnabled: "SharedOnline.React18RenderTooltipFixEnabled",
  react18AppThemeProviderProxyEnabled:
    "Microsoft.Office.SharedOnline.React18AppThemeProviderProxyEnabled",
  react18RenderFixEnabled: "SharedOnline.ChangeGate.React18RenderFixEnabled",
  react18RenderTaskPaneFixEnabled:
    "SharedOnline.ChangeGate.React18RenderTaskPaneFixEnabled",
  react18RenderHeaderButtonFixEnabled:
    "Microsoft.Office.SharedOnline.React18RenderHeaderButtonFixEnabled",
  react18RenderCanvasContextualTooltipsEnabled:
    "Microsoft.Office.SharedOnline.React18RenderCanvasContextualTooltipsEnabled",
};

// List of all shield change/feature gates that must be checked at the acui level. If your fix can be checked at the ooux level or app level, use ShieldSettings.ts
const shieldExperiments = {
  draggableFloatieMoveFixIsEnabled:
    "SharedOnline.ChangeGate.DraggableFloatieMoveFixIsEnabled",
  contextMenuUnmountFixIsEnabled:
    "SharedOnline.ChangeGate.ContextMenuUnmountFixIsEnabled",
  undefinedMenuItemFixEnabled:
    "SharedOnline.ChangeGate.UndefinedMenuItemFixEnabled",
  inputArrowKeyNavFixEnabled:
    "SharedOnline.ChangeGate.InputArrowKeyNavFixEnabled",
  spinButtonDisabledStylesEnabled:
    "SharedOnline.ChangeGate.SpinButtonDisabledStylesEnabled",
  splitButtonInMenuDoubleExecuteFixEnabled:
    "SharedOnline.ChangeGate.SplitButtonInMenuDoubleExecuteFixEnabled",
  fontPreviewGalleryDismissFixEnabled:
    "SharedOnline.ChangeGate.FontPreviewGalleryDismissFixEnabled",
  floatieCaretButtonStylesFixEnabled:
    "SharedOnline.ChangeGate.FloatieCaretButtonStylesFixEnabled",
  paddlesZIndexIsEnabled: "SharedOnline.ChangeGate.PaddlesZIndexIsEnabled",
  wideBorderForSelectedButtonEnabled:
    "SharedOnline.ChangeGate.WideBorderForSelectedButtonEnabled",
  shouldFocusOnContainerEnabled:
    "SharedOnline.ChangeGate.ShouldFocusOnContainerEnabled",
  expandFontPickerSubmenuFixEnabled:
    "SharedOnline.ChangeGate.ExpandFontPickerSubmenuFixEnabled",
  fixSplitToggleButtonStylesEnabled:
    "SharedOnline.ChangeGate.FixSplitToggleButtonStylesEnabled",
  ...react18Experiments,
};

const consumerExperiments = {
  displayFreeFontsFirstFlightEnabled:
    "Microsoft.Office.WordOnline.DisplayFreeFontsFirst",
  isProTipSuggestionEnabled:
    "Microsoft.Office.WordOnline.ProTipSuggestionEnabled",
};

const unAuthExperiments = {
  isSISURibbonEnabled: "IsSISURibbonEnabled",
};

export const ribbonExperiments = {
  renderInitiallyEnabledRibbonIsEnabled:
    "RenderInitiallyEnabledRibbonIsEnabled",
  updatedTellMePlaceholderIsEnabled: "UpdatedTellMePlaceholderIsEnabled",
  multilineRibbonIsEnabled: "ReactMultilineRibbonIsEnabled",
  taskPaneResizingIsEnabled: "TaskPaneResizingIsEnabled",
  activateComboAndSpinControlOnEnter:
    "ActivateComboAndSpinMenuItemOnEnterIsEnabled",
  chatPPTEnabled: "Microsoft.Office.PowerPointOnline.ChatPPTOnline",
  ribbonCachedScalingIsEnabled: "ReactRibbonCachedScalingIsEnabled",
  disableRibbonAnimationComponents: "DisableAnimationComponentsIsEnabled",
  delayInstallAddInIsEnabled: "DelayInstallAddInIsEnabled",
  tooltipsTargetOverflowSetItemEnabled: "TooltipsTargetOverflowSetItemEnabled",
  disableColorPickerFancyTooltips: "DisableColorPickerFancyTooltips",
  isDisabledTooltipsEnabled: "SharedOnline.DisabledTooltipsEnabled",
  batchTopBarScaleDataInRibbon: "BatchTopBarScaleDataInRibbon",
  decentralizedScalingLogicIsEnabled:
    "Microsoft.Office.SharedOnline.DecentralizedScalingLogicIsEnabled",
  perfMarkerThrottlingOverrideIsEnabled:
    "Microsoft.Office.SharedOnline.PerfMarkerThrottlingOverrideIsEnabled",
  shouldContextualTabUseNormalTabStyles:
    "ShouldContextualTabUseNormalTabStyles",
  zoomSliderIsEnabled: "ZoomSliderIsEnabled",
  shouldMergeAppToggleButtonMenuItemStyles:
    "ShouldMergeAppToggleButtonMenuItemStyles",
  multiWindowSupportIsEnabled:
    "SharedOnline.ChangeGate.MultiWindowSupportIsEnabled",
  customAnonymousNameIsEnabled:
    "Microsoft.Office.SharedOnline.IsCustomAnonymousNameEnabled",
  ribbonBottomBarMemoryLeakFix:
    "SharedOnline.ChangeGate.RibbonBottomBarMemoryLeakFix",
  comboBoxShadowRenderingFix:
    "SharedOnline.ChangeGate.ComboBoxShadowRenderingFix",
  useFullDescriptionLabelOnly:
    "SharedOnline.ChangeGate.UseFullDescriptionLabelOnly",
  closeMenuOnUnmount: "SharedOnline.ChangeGate.CloseMenuOnUnmount",
  appTooltipAppContextIsEnabled:
    "SharedOnline.ChangeGate.AppTooltipAppContextIsEnabled",
  headerIcon: "SharedOnline.ChangeGate.HeaderIcon",
  singleLineToolBar: "SharedOnline.IsSingleLineToolbarEnabled",
  dismissHostingSurfaceOnExecuteFixEnabled:
    "SharedOnline.ChangeGate.DismissHostingSurfaceOnExecuteFixEnabled",
  controlIdLoggingIsEnabled:
    "SharedOnline.ChangeGate.OfficeVSO_8561837_EnableControlIDDatafields",
  controlsPerLayoutScalingFix:
    "SharedOnline.ChangeGate.ControlsPerLayoutScalingFix",
  globalThemePaddingFix: "SharedOnline.ChangeGate.GlobalThemePaddingFix",
  appGalleryPreviewButtonIdIsEnabled: "AppGalleryPreviewButtonIdIsEnabled",
  mfpRemoveUnnecessaryRequestAnimationFrame:
    "Microsoft.Office.SharedOnline.ModernFontPickerRemoveUnnecessaryRequestAnimationFrameIsEnabled",
  mfpDeferOnMenuDismissedEnabled:
    "Microsoft.Office.SharedOnline.ModernFontPickerDeferOnMenuDismissedIsEnabled",
  copilotMaxTaskpaneWidthIsEnabled:
    "Microsoft.Office.WordOnline.CopilotTaskPaneMaxWidth",
  messageBarUpdatesEnabled: "SharedOnline.ChangeGate.MessageBarUpdatesEnabled",
  paddlesFixIsEnabled: "SharedOnline.ChangeGate.PaddlesFixIsEnabled",
  appGallerySplitButtonStylesFixEnabled:
    "SharedOnline.ChangeGate.AppGallerySplitButtonTextAlignmentFixEnabled",
  updatedPaddleOffsetEnabled:
    "SharedOnline.ChangeGate.UpdatedPaddleOffsetEnabled",
  menuButtonIconForLargeFlyoutAnchorsEnabled:
    "SharedOnline.ChangeGate.MenuButtonIconForLargeFlyoutAnchorsEnabled",
  isNewFluentIconsEnabled: "Microsoft.Office.SharedOnline.FluentIconsEnabled",
  v9MenuAutoDismissFixEnabled:
    "SharedOnline.ChangeGate.v9MenuAutoDismissFixEnabled",
  isMenuButtonRefFixEnabled:
    "SharedOnline.ChangeGate.OfficeVSO:8941200_v9menuButtonRefFixEnabled",
  fixIncorrectMenuShownDuringSearch:
    "SharedOnline.ChangeGate.FixIncorrectMenuShownDuringSearch",
  fixOnMenuOpenedTriggerOnSubmenuHover:
    "SharedOnline.ChangeGate.FixOnMenuOpenedTriggerOnSubmenuHover",
  isFluentV9DispatchOptimizationEnabled:
    "SharedOnline.FluentV9DispatchOptimizationEnabled",
  isFluentV9FloatieDispatchOptimizationEnabled:
    "SharedOnline.FluentV9FloatieDispatchOptimizationEnabled",
  hideCustomTooltipInV9AppControlsEnabled:
    "SharedOnline.ChangeGate.HideCustomTooltipInV9AppControlsEnabled",
  triggerOnMenuDismissedOnSubmenuClose:
    "SharedOnline.ChangeGate.TriggerOnMenuDismissedOnSubmenuClose",
  isCloseBtnTabKeyDownOnResizeHandleEnabled:
    "SharedOnline.ChangeGate.isCloseBtnTabKeyDownOnResizeHandleEnabled",
  fluentV9ColorPickerIsEnabled: "SharedOnline.FluentV9ColorPickerEnabled",
  fluentV9PreviewGalleryIsEnabled: "SharedOnline.FluentV9PreviewGalleryEnabled",
  insertTableGridFluentV9IsEnabled:
    "SharedOnline.InsertTableGridFluentV9IsEnabled",
  isShadowOverflowFixEnabled:
    "SharedOnline.ChangeGate.IsShadowOverflowFixEnabled",
  isConditionForV9StandaloneCMEnabled:
    "SharedOnline.ChangeGate.IsConditionForV9StandaloneCMEnabled",
  isFileMenuExtraPaddingFixEnabled:
    "SharedOnline.ChangeGate.OfficeVSO:9133405_FileMenuExtraPaddingFixEnabled",
  isStopKeyDownEventPropagationInV9MenuEnabled:
    "SharedOnline.ChangeGate.StopKeyDownEventPropagationInV9MenuEnabled",
  isContextualApiTelemetryLevelUpdateEnabled:
    "SharedOnline.ChangeGate.ContextualApiTelemetryLevelUpdateEnabled",
  minimumTargetSizesEnabled:
    "Microsoft.Office.SharedOnline.MinimumTargetSizesEnabled",
  trackOOUXAsyncUiEnabled: "TrackOOUXAsyncUiEnabled",
  useFileMenuIdForAsyncUI:
    "SharedOnline.ChangeGate.OfficeVSO:9429092_UseFileMenuIdForAsyncUI",
  isHighContrastModeEnabled: "SharedUXHighContrastThemeIsEnabled",
  isHighContrastThemeOn: "HighContrastThemeIsOn",
  isDescriptionReadoutWithoutLabelEnabled:
    "SharedOnline.ChangeGate.DescriptionReadoutWithoutLabelEnabled",
  isfileMenuRegressionUpdateEnabled: "SharedOnline.FileMenuRegressionUpdate",
  isV9ContextMenuPositionFixEnabled:
    "SharedOnline.isV9ContextMenuPositionFixEnabled",
  isV8AppContextMenuSlideFixEnabled:
    "SharedOnline.isV8AppContextMenuSlideFixEnabled",
  isExposeMenuOpenEventForMFPEnabled:
    "SharedOnline.ChangeGate.ExposeMenuOpenEventForMFPEnabled",
  openToDesktopInL1Enabled: "SharedOnline.OpenToDesktopInL1Enabled",
  openToDesktopInL2ExportEnabled: "SharedOnline.OpenToDesktopInL2ExportEnabled",
  isContextMenuAndFloatieRightClickDisabled:
    "SharedOnline.ChangeGate.ContextMenuAndFloatieRightClickDisabled",
  isSplitButtonIgnoreFastDisableEnabled:
    "SharedOnline.IsSplitButtonIgnoreFastDisableEnabled",
  isFavoritesInFileMenuEnabled: "SharedOnline.IsFavoritesInFileMenuEnabled",
  appFlyoutAnchorRestoreFocusFixEnabled:
    "SharedOnline.AppFlyoutAnchorRestoreFocusFixEnabled",
  headlessThemingEnabled: "SharedOnline.HeadlessThemingEnabled",
  dialogScrollBarOverlappingFixEnabled:
    "SharedOnline.ChangeGate.DialogScrollBarOverlappingFixEnabled",
  floatieDismissOnCtrlFixEnabled: "SharedOnline.FloatieDismissOnCtrlFixEnabled",
  persistedPanesFixEnabled: "SharedOnline.ChangeGate.PersistedPanesFixEnabled",
  programaticMenuOpensEnabled:
    "SharedOnline.ChangeGate.ProgramaticMenuOpensEnabled",
  overflowingStylesPreviewFixIsEnabled:
    "Microsoft.Office.SharedOnline.OverflowingStylesPreviewFixIsEnabled",
  ...shieldExperiments,
  ...consumerExperiments,
  ...staticRibbonExperiments,
  ...fluentExperiments,
  ...unAuthExperiments,
};

export const useHighContrast = (context: ExperimentChecker) => {
  return (
    !!checkExperiment(context, ribbonExperiments.isHighContrastModeEnabled) &&
    !!checkExperiment(context, ribbonExperiments.isHighContrastThemeOn)
  );
};

export const platformFeatures = {
  disableDelayRenderToolTipMultiWindow:
    "DisableDelayRenderTooltipForMultiWindow",
  disableTaskpaneRefreshStyling: "DisableTaskpaneRefreshStyling",
  modalContextMenuIsEnabled: "ModalContextMenuIsEnabled",
  renderV9MenuAndFloatieTogetherEnabled:
    "RenderV9MenuAndFloatieTogetherEnabled",
  isSchedulingUpdateCommandUIOnMenuPositionedEnabled:
    "SchedulingUpdateCommandUIOnMenuPositionedEnabled",
  tooltipForMacIsEnabled: "TooltipForMacIsEnabled",
};

export const checkExperiment = (
  context: ExperimentChecker,
  feature: string
) => {
  return checkExperimentBase(context, feature);
};

export const isShyRibbonEnabled = (context: ExperimentChecker) => {
  return isShyRibbonFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isChatPPTEnabled = (context: ExperimentChecker) => {
  return isChatPPTFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isShyRibbonFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return isShyRibbonFeatureEnabledGeneric(isFeatureEnabled);
};

export const isChatPPTFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return (
    isFeatureEnabled !== undefined &&
    isFeatureEnabled(ribbonExperiments.chatPPTEnabled)
  );
};
